/**
 * RollupFieldType — F12.B T12.B.4.
 * Pole read-only obliczające agregat (SUM/COUNT/AVG/MAX/MIN) po rekordach z relacji.
 * Wymaga zapisanego rekordu (recordId).
 */
import { useQuery } from '@tanstack/react-query';
import { Calculator, Loader2 } from 'lucide-react';

import { api } from '@/lib/api';

import { FieldRegistry } from '../FieldRegistry';

const LABELS: Record<string, string> = {
  SUM: 'Suma',
  AVG: 'Średnia',
  COUNT: 'Liczba',
  MAX: 'Maksimum',
  MIN: 'Minimum',
};

interface RollupResponse {
  value: number | null;
  count: number;
  totalRecords: number;
  function: string;
}

FieldRegistry.register({
  type: 'rollup',
  renderInput: ({ field, recordId }) => {
    const enabled = !!recordId;
    const { data, isLoading, isError } = useQuery<RollupResponse>({
      queryKey: ['rollup', field.id, recordId],
      queryFn: async () => {
        const res = await api.get<RollupResponse>(
          `/modules/${field.moduleId}/records/${recordId}/rollup/${field.id}`,
        );
        return res.data;
      },
      enabled,
      staleTime: 30_000,
    });

    const aggFn =
      (field.validationRules?.['rollup_aggregate_function'] as string | undefined) ?? 'SUM';
    const fnLabel = LABELS[aggFn] ?? aggFn;

    if (!recordId) {
      return (
        <div className="rounded-lg border border-border-primary bg-bg-secondary px-3 py-2 text-sm italic text-text-muted">
          Zapisz rekord aby zobaczyć wartość rollup
        </div>
      );
    }
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 rounded-lg border border-border-primary bg-bg-secondary px-3 py-2">
          <Loader2 className="h-4 w-4 animate-spin text-accent-600" />
          <span className="text-sm text-text-secondary">Obliczanie…</span>
        </div>
      );
    }
    if (isError) {
      return (
        <div className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-600">
          Błąd obliczania wartości
        </div>
      );
    }

    const val = data?.value ?? 0;
    return (
      <div className="rounded-lg border border-accent-200 bg-accent-50 px-3 py-2">
        <div className="mb-1 flex items-center gap-1 text-xs font-medium text-accent-600">
          <Calculator className="h-3 w-3" />
          {fnLabel} z {data?.count ?? 0} rekordów
        </div>
        <p className="text-2xl font-bold text-accent-900">
          {val != null ? val.toLocaleString('pl-PL') : '—'}
        </p>
        {data && data.count !== data.totalRecords && (
          <p className="mt-1 text-xs text-accent-600">
            {data.count} z {data.totalRecords} rekordów ma wartość
          </p>
        )}
      </div>
    );
  },
  renderCell: (field, value) => {
    const aggFn =
      (field.validationRules?.['rollup_aggregate_function'] as string | undefined) ?? 'SUM';
    return (
      <div className="flex items-center gap-2">
        <Calculator className="h-3 w-3 flex-shrink-0 text-accent-500" />
        <div>
          <div className="text-xs font-medium text-text-primary">
            {value != null ? Number(value).toLocaleString('pl-PL') : '—'}
          </div>
          <div className="text-xs text-text-muted">{LABELS[aggFn] ?? aggFn}</div>
        </div>
      </div>
    );
  },
  validate: () => null,
  defaultValue: null,
});
